import { useEffect, useState, useRef, useCallback } from "react";

import {
  fetchNotifications,
  markNotificationRead,
  deleteNotification
} from "../services/notificationService";

export default function useNotifications() {

  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  const previousIds = useRef(new Set());
  const loading = useRef(false);
  const initialized = useRef(false);
  const failCount = useRef(0);

  /* =================================
     LOAD NOTIFICATIONS
  ================================= */

  const loadNotifications = useCallback(async () => {

    if (loading.current) return;
    if (failCount.current >= 4) return; // server is down, stop hammering it

    // don't call if user isn't logged in yet
    const user = localStorage.getItem("adm_panel_user");
    if (!user) return;

    loading.current = true;

    try {

      const data = await fetchNotifications();

      const visible = (data || []).filter(n => n.status !== "hidden");

      setNotifications(visible);

      setUnreadCount(
        visible.filter(n => n.status === "unread").length
      );

      /* =========
         NEW TOAST
      ========= */

      visible.forEach(n => {

        const alreadyKnown = previousIds.current.has(n.id);

        previousIds.current.add(n.id);

        // 👇 toast sadece ilk load sonrası
        if (initialized.current && !alreadyKnown) {

          window.dispatchEvent(
            new CustomEvent("new-notification", {
              detail: n
            })
          );

        }

      });

      failCount.current = 0; // reset on success
      initialized.current = true;

    } catch (err) {

      failCount.current += 1;
      if (failCount.current <= 3) {
        console.error("notification fetch error", err);
      } else if (failCount.current === 4) {
        console.warn("Notifications: server unreachable, stopping retries.");
      }

    } finally {

      loading.current = false;

    }

  }, []);

  /* =================================
     POLLING
  ================================= */

  useEffect(() => {

    loadNotifications();

    const interval = setInterval(loadNotifications, 60000); // 60s — free tier DB doesn't need hammering

    return () => clearInterval(interval);

  }, [loadNotifications]);

  /* =================================
     MARK AS READ
  ================================= */

  const markAsRead = async (notif) => {

    if (notif.status === "read") return;

    try {

      await markNotificationRead(notif.notificationId);

      setNotifications(prev =>
        prev.map(n =>
          n.id === notif.id ? { ...n, status: "read" } : n
        )
      );

      setUnreadCount(prev => Math.max(prev - 1, 0));

    } catch (err) {

      console.error("mark read error", err);

    }

  };

  /* =================================
     DELETE
  ================================= */

  const removeNotification = async (notif) => {

    try {

      await deleteNotification(notif.notificationId);

      setNotifications(prev =>
        prev.filter(n => n.id !== notif.id)
      );

      if (notif.status === "unread") {
        setUnreadCount(prev => Math.max(prev - 1, 0));
      }

    } catch (err) {

      console.error("delete notification error", err);

    }

  };

  return {
    notifications,
    unreadCount,
    reload: loadNotifications,
    markAsRead,
    deleteNotification: removeNotification
  };

}